import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getDynamicPositionAttributes } from '~/modules/common/charts/timeline/calculations';
import { useHasPermission } from '~/modules/common/permissions';
import { ResourceAllocationStatus } from '~/types';
import {
  TimelineBlock,
  DragEndIndicator,
  DragStartIndicator
} from '~/modules/resourcing/common/chart/components';
import { RESIZE_DIRECTION_ENUM } from '~/modules/resourcing/common/enums';
import { useQuickAllocationContext } from '~/modules/quickAllocation/components/QuickAllocationUserChart/hooks';
import {
  getDayViewRoundedHours,
  getDayViewRoundedPercentage
} from '~/modules/resourcing/common/util/scheduleUtil';
import useStyles from './useStyles';

const QuickAllocationUserProjectSummaryBlock = ({
  startDate,
  endDate,
  allocatedHours,
  resizeDirection,
  dragDelta,
  gestureBindEvents,
  firstPeriod,
  lastPeriod,
  resourceAllocation,
  scheduledHours,
  overlayPeriods,
  handleAllocationPeriodClick,
  dates
}) => {
  const { chartStartDate, scale } = useQuickAllocationContext();

  const isResourceAssignmentEnabled = useHasPermission({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });

  const isCompleteResourceAllocationPermissionEnabled = useHasPermission({
    actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
  });

  const isEditable =
    (resourceAllocation.allocationStatus ===
      ResourceAllocationStatus.Committed &&
      isCompleteResourceAllocationPermissionEnabled) ||
    (isResourceAssignmentEnabled &&
      resourceAllocation.allocationStatus !==
        ResourceAllocationStatus.Committed);

  const { dynamicPosition } = useMemo(
    () =>
      getDynamicPositionAttributes({
        chartStartDate,
        start: startDate,
        end: endDate,
        scale
      }),
    [startDate, endDate, chartStartDate, scale]
  );

  const showPercentage = scheduledHours > 0;
  const loading = scheduledHours ? (allocatedHours * 100) / scheduledHours : 0;

  const classes = useStyles();

  const dragIndicatorClasses = useMemo(
    () => ({
      resizeHandle: classes.resizeHandle,
      resizeHandleStart: classes.resizeHandleStart,
      resizeHandleEnd: classes.resizeHandleEnd,
      resizeIcon: classes.resizeIcon
    }),
    [classes]
  );
  const dynamicPositionWithDragIndicators = useMemo(() => {
    const leftMostPeriod =
      firstPeriod && resizeDirection === RESIZE_DIRECTION_ENUM.START;
    const rightMostPeriod =
      lastPeriod && resizeDirection === RESIZE_DIRECTION_ENUM.END;

    return {
      top: dynamicPosition.top,
      left: leftMostPeriod
        ? dragDelta + dynamicPosition.left
        : dynamicPosition.left,
      width: leftMostPeriod
        ? dynamicPosition.width - dragDelta
        : rightMostPeriod
        ? dynamicPosition.width + dragDelta
        : dynamicPosition.width
    };
  }, [firstPeriod, resizeDirection, lastPeriod, dynamicPosition, dragDelta]);

  const handleClickAndKeyPress = useCallback(
    event => {
      if (
        (event.type === 'click' ||
          event.key === 'Enter' ||
          event.keyCode === 13) &&
        isEditable &&
        handleAllocationPeriodClick
      ) {
        handleAllocationPeriodClick({
          event,
          startDate,
          endDate,
          isOverlay: false,
          overlayPeriods,
          allocation: resourceAllocation,
          totalAllocatedHours: allocatedHours,
          totalScheduledHours: scheduledHours
        });
      }
    },
    [
      isEditable,
      handleAllocationPeriodClick,
      startDate,
      endDate,
      overlayPeriods,
      resourceAllocation,
      allocatedHours,
      scheduledHours
    ]
  );

  const percentage = getDayViewRoundedPercentage({
    percentage: loading,
    scale
  });

  const totalHours = getDayViewRoundedHours({ hours: allocatedHours, scale });

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootRejected]:
          resourceAllocation.allocationStatus ===
          ResourceAllocationStatus.Rejected,
        [classes.rootDraft]:
          resourceAllocation.allocationStatus ===
          ResourceAllocationStatus.Draft,
        [classes.rootDPropsed]:
          resourceAllocation.allocationStatus ===
          ResourceAllocationStatus.Proposed,
        [classes.removeHoverEffect]: !isEditable
      })}
      style={dynamicPositionWithDragIndicators}
    >
      <div
        className={classes.timeBlock}
        onClick={isEditable ? handleClickAndKeyPress : null}
        tabIndex="0"
        onKeyPress={isEditable ? handleClickAndKeyPress : null}
        role="button"
        aria-hidden="true"
      >
        <TimelineBlock
          hours={totalHours}
          percentage={percentage}
          showPercentage={showPercentage}
        />
      </div>
      {firstPeriod && gestureBindEvents && isEditable && (
        <DragStartIndicator
          classes={dragIndicatorClasses}
          gestureBindEvents={gestureBindEvents}
          showTooltip
          startDate={dates.startDate}
        />
      )}
      {lastPeriod && gestureBindEvents && isEditable && (
        <DragEndIndicator
          classes={dragIndicatorClasses}
          gestureBindEvents={gestureBindEvents}
          showTooltip
          endDate={dates.endDate}
        />
      )}
    </div>
  );
};

QuickAllocationUserProjectSummaryBlock.propTypes = {
  startDate: PropTypes.object.isRequired,
  endDate: PropTypes.object.isRequired,
  allocatedHours: PropTypes.number.isRequired,
  firstPeriod: PropTypes.bool.isRequired,
  lastPeriod: PropTypes.bool.isRequired,
  resizeDirection: PropTypes.string,
  dragDelta: PropTypes.number,
  gestureBindEvents: PropTypes.func,
  resourceAllocation: PropTypes.object,
  scheduledHours: PropTypes.number,
  overlayPeriods: PropTypes.array,
  handleAllocationPeriodClick: PropTypes.func,
  dates: PropTypes.object
};

export default QuickAllocationUserProjectSummaryBlock;
